/**
 * Help Center Localized Content Index
 *
 * Provides localized Help Center content based on language
 */

import type { HelpArticle, HelpCategory } from "../types";

// Russian locale
import {
  HELP_CATEGORIES_RU,
  ALL_ARTICLES_RU,
  getArticlesByCategoryRu,
  getArticleBySlugRu,
  getArticleByIdRu,
  getPopularArticlesRu,
  searchArticlesRu,
  getCategoryWithArticlesRu,
} from "./ru";

// Spanish locale
import {
  HELP_CATEGORIES_ES,
  ALL_ARTICLES_ES,
  getArticlesByCategoryEs,
  getArticleBySlugEs,
  getArticleByIdEs,
  getPopularArticlesEs,
  searchArticlesEs,
  getCategoryWithArticlesEs,
} from "./es";

// English (default) - import from parent
import { HELP_CATEGORIES } from "../categories";
import {
  ALL_ARTICLES,
  getArticlesByCategory,
  getArticleBySlug,
  getArticleById,
  getPopularArticles,
  searchArticles,
} from "../index";

export type SupportedLocale = "en" | "ru" | "es";

/**
 * Get categories for a specific locale
 */
export function getLocalizedCategories(locale: SupportedLocale): HelpCategory[] {
  switch (locale) {
    case "ru":
      return HELP_CATEGORIES_RU;
    case "es":
      return HELP_CATEGORIES_ES;
    case "en":
    default:
      return HELP_CATEGORIES;
  }
}

/**
 * Get all articles for a specific locale
 */
export function getLocalizedArticles(locale: SupportedLocale): HelpArticle[] {
  switch (locale) {
    case "ru":
      return ALL_ARTICLES_RU;
    case "es":
      return ALL_ARTICLES_ES;
    case "en":
    default:
      return ALL_ARTICLES;
  }
}

/**
 * Get articles by category for a specific locale
 */
export function getLocalizedArticlesByCategory(
  locale: SupportedLocale,
  categoryId: string
): HelpArticle[] {
  switch (locale) {
    case "ru":
      return getArticlesByCategoryRu(categoryId);
    case "es":
      return getArticlesByCategoryEs(categoryId);
    case "en":
    default:
      return getArticlesByCategory(categoryId);
  }
}

/**
 * Get article by slug for a specific locale
 */
export function getLocalizedArticleBySlug(
  locale: SupportedLocale,
  slug: string
): HelpArticle | undefined {
  switch (locale) {
    case "ru":
      return getArticleBySlugRu(slug);
    case "es":
      return getArticleBySlugEs(slug);
    case "en":
    default:
      return getArticleBySlug(slug);
  }
}

/**
 * Get article by ID for a specific locale
 */
export function getLocalizedArticleById(
  locale: SupportedLocale,
  id: string
): HelpArticle | undefined {
  switch (locale) {
    case "ru":
      return getArticleByIdRu(id);
    case "es":
      return getArticleByIdEs(id);
    case "en":
    default:
      return getArticleById(id);
  }
}

/**
 * Get popular articles for a specific locale
 */
export function getLocalizedPopularArticles(locale: SupportedLocale): HelpArticle[] {
  switch (locale) {
    case "ru":
      return getPopularArticlesRu();
    case "es":
      return getPopularArticlesEs();
    case "en":
    default:
      return getPopularArticles();
  }
}

/**
 * Search articles for a specific locale
 */
export function searchLocalizedArticles(
  locale: SupportedLocale,
  query: string
): HelpArticle[] {
  switch (locale) {
    case "ru":
      return searchArticlesRu(query);
    case "es":
      return searchArticlesEs(query);
    case "en":
    default:
      return searchArticles(query);
  }
}

/**
 * Get category with articles for a specific locale
 */
export function getLocalizedCategoryWithArticles(
  locale: SupportedLocale,
  categoryId: string
): { category: HelpCategory; articles: HelpArticle[] } | undefined {
  switch (locale) {
    case "ru":
      return getCategoryWithArticlesRu(categoryId);
    case "es":
      return getCategoryWithArticlesEs(categoryId);
    case "en":
    default: {
      const category = HELP_CATEGORIES.find((cat) => cat.id === categoryId);
      if (!category) return undefined;
      const articles = getArticlesByCategory(categoryId);
      return { category, articles };
    }
  }
}
